import { formatPayMonthLabel } from "./runPayrollUtils";

export function getCompanyName() {
  try {
    const raw = localStorage.getItem("companyData");
    if (!raw) return "Company";
    const data = JSON.parse(raw);
    return data?.name || data?.companyName || data?.legalName || "Company";
  } catch {
    return "Company";
  }
}

export function formatDateCell(v) {
  if (!v) return "";
  const s = String(v).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/** July-2026 */
export function fileMonthLabel(payMonth) {
  const s = String(payMonth || "").slice(0, 7);
  const m = s.match(/^(\d{4})-(\d{2})$/);
  if (!m) return s || "Month";
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return `${months[Number(m[2]) - 1] || m[2]}-${m[1]}`;
}

export function styleLightHeader(cell, fillArgb) {
  cell.font = { bold: true, color: { argb: "FF1F2937" }, size: 10 };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fillArgb } };
  cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  cell.border = {
    top: { style: "thin", color: { argb: "FFD1D5DB" } },
    bottom: { style: "thin", color: { argb: "FFD1D5DB" } },
    left: { style: "thin", color: { argb: "FFD1D5DB" } },
    right: { style: "thin", color: { argb: "FFD1D5DB" } },
  };
}

export function applyDataBorder(cell) {
  cell.border = {
    top: { style: "thin", color: { argb: "FFE5E7EB" } },
    bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
    left: { style: "thin", color: { argb: "FFE5E7EB" } },
    right: { style: "thin", color: { argb: "FFE5E7EB" } },
  };
}

export function moneyFmt(cell) {
  cell.numFmt = "#,##0";
  cell.alignment = { horizontal: "right" };
}

/** Whole rupees for Quick Reports exports */
export function roundMoney(n) {
  return Math.round(Number(n || 0));
}

export function round2safe(n) {
  return Math.round(Number(n || 0));
}

export function hideSheetGridlines(ws) {
  if (!ws) return;
  const existing = Array.isArray(ws.views) && ws.views.length ? ws.views : [{}];
  ws.views = existing.map((v) => ({
    ...v,
    showGridLines: false,
  }));
  try {
    ws.properties = { ...(ws.properties || {}), showGridLines: false };
  } catch {
    /* ignore */
  }
}

/**
 * Original Quick Reports column widths (header / fixed rules only — not cell values).
 * Matches the first export layout so money columns stay compact.
 */
export function applyHeaderBasedColumnWidths(ws, headers = [], opts = {}) {
  if (!ws || !Array.isArray(headers) || !headers.length) return;
  const layout = opts.layout || "auto";

  const isRegister =
    layout === "register" ||
    (layout === "auto" && /^sl\s*no$/i.test(String(headers[0] || "")));
  const isReco =
    layout === "reco" ||
    (layout === "auto" && /^(emp\s*id)$/i.test(String(headers[0] || "")));
  const isBank =
    layout === "bank" ||
    (layout === "auto" && /sl\s*no/i.test(String(headers[0] || "")) && /net payable/i.test(String(headers[headers.length - 1] || "")));
  const isJv =
    layout === "jv" ||
    (layout === "auto" && /account|particulars/i.test(String(headers[1] || "")));
  const isSummary =
    layout === "summary" ||
    (layout === "auto" && /^particulars$/i.test(String(headers[0] || "")));

  if (isRegister) {
    ws.getColumn(1).width = 8;
    ws.getColumn(2).width = 14;
    ws.getColumn(3).width = 26;
    for (let c = 4; c <= headers.length; c += 1) {
      ws.getColumn(c).width = Math.min(22, Math.max(11, String(headers[c - 1]).length + 2));
    }
    return;
  }

  if (isReco) {
    ws.getColumn(1).width = 14;
    ws.getColumn(2).width = 28;
    for (let c = 3; c <= headers.length - 2; c += 1) {
      ws.getColumn(c).width = 12;
    }
    if (headers.length >= 2) {
      ws.getColumn(headers.length - 1).width = 18; // Reason Category
      ws.getColumn(headers.length).width = 42; // Reason
    }
    return;
  }

  if (isBank) {
    [10, 28, 18, 18, 14, 14].forEach((w, i) => {
      if (i < headers.length) ws.getColumn(i + 1).width = w;
    });
    return;
  }

  if (isJv) {
    [10, 44, 12, 14, 14, 36].forEach((w, i) => {
      if (i < headers.length) ws.getColumn(i + 1).width = w;
    });
    return;
  }

  if (isSummary) {
    [36, 14, 14, 36].forEach((w, i) => {
      if (i < headers.length) ws.getColumn(i + 1).width = w;
    });
    return;
  }

  // Fallback: modest header-sized widths
  headers.forEach((h, i) => {
    ws.getColumn(i + 1).width = Math.min(22, Math.max(11, String(h || "").length + 2));
  });
}

/** @deprecated alias */
export function autoFitColumns(ws, options = {}) {
  applyHeaderBasedColumnWidths(ws, options.headers || [], options);
}

export function finalizeReportWorksheet(ws) {
  if (!ws) return;
  hideSheetGridlines(ws);
}

export function finalizeReportWorkbook(wb) {
  if (!wb) return;
  wb.eachSheet((ws) => finalizeReportWorksheet(ws));
}

export function addTitleBlock(ws, companyName, subtitle, meta, colCount) {
  const titleRow = ws.addRow([companyName]);
  titleRow.font = { bold: true, size: 15, color: { argb: "FF1E3A5F" } };
  ws.mergeCells(1, 1, 1, Math.max(colCount, 4));

  const subRow = ws.addRow([subtitle]);
  subRow.font = { bold: true, size: 12, color: { argb: "FF374151" } };
  ws.mergeCells(2, 1, 2, Math.max(colCount, 4));

  const metaRow = ws.addRow([meta]);
  metaRow.font = { size: 9, italic: true, color: { argb: "FF6B7280" } };
  ws.mergeCells(3, 1, 3, Math.max(colCount, 4));

  ws.addRow([]);
}

export const REPORT_COLORS = {
  basic: "FFDBEAFE",
  structure: "FFD1FAE5",
  attendance: "FFEDE9FE",
  earnings: "FFFEF3C7",
  bank: "FFFCE7F3",
  employer: "FFE0E7FF",
  jvDebit: "FFFEE2E2",
  jvCredit: "FFDCFCE7",
  recon: "FFE0F2FE",
  summary: "FFCCFBF1",
};

export function mergeComponentOrder(preferred, live) {
  const liveList = (live || []).filter((n) => !/^lop\s*deduction$/i.test(n));
  const seen = new Set();
  const out = [];
  (preferred || []).forEach((n) => {
    if (liveList.includes(n) && !seen.has(n)) {
      seen.add(n);
      out.push(n);
    }
  });
  liveList.forEach((n) => {
    if (!seen.has(n)) {
      seen.add(n);
      out.push(n);
    }
  });
  return out;
}

export async function downloadWorkbook(wb, filename) {
  finalizeReportWorkbook(wb);
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  return filename;
}

export function buildRegisterSheet(wb, {
  sheetName = "Salary Register",
  title,
  metaLine,
  companyName,
  rows,
  orderedFixedEarnings = [],
  orderedBenefits = [],
  orderedPaidFixed = [],
  orderedVariables = [],
  orderedDeductions = [],
  /** @deprecated use orderedFixedEarnings + orderedBenefits */
  orderedFixed = null,
  /** @deprecated use orderedPaidFixed + orderedVariables */
  orderedEarnings = null,
  includeTotalCost = true,
  includeTotalCashFlow = false,
}) {
  const COLORS = REPORT_COLORS;
  // Backward-compatible fallbacks if callers still pass old props
  const fixedEarnings =
    orderedFixedEarnings.length || !orderedFixed
      ? orderedFixedEarnings
      : orderedFixed;
  const benefits = orderedBenefits;
  const paidFixed =
    orderedPaidFixed.length || !orderedEarnings
      ? orderedPaidFixed
      : orderedEarnings;
  const variables = orderedVariables;

  const basicHeaders = [
    "Sl No",
    "Emp Code",
    "Name",
    "DOJ",
    "DOE",
    "Department",
    "Designation",
    "Branch",
    "PAN",
    "Aadhar",
    "UAN",
    "PF Account No",
    "ESI No",
  ];
  const fixedEarningHeaders = [...fixedEarnings, "Gross Salary (E)"];
  const benefitHeaders = [
    ...benefits,
    "Total Benefits (B)",
    "Monthly CTC",
    "Annual CTC",
  ];
  const attendanceHeaders = [
    "Calendar Days",
    "Paid Days",
    "Present Days",
    "LOP Days",
    "LOP Reversal Days",
  ];
  const paidFixedHeaders = [...paidFixed];
  // Drop legacy per-day LOP Reversal columns; Arrears is the standard head.
  const variableHeaders = [
    ...variables.filter((n) => !/lop\s*reversal\s*\(/i.test(String(n))),
    ...(variables.some((n) => /^arrears$/i.test(String(n))) ? [] : ["Arrears"]),
  ];
  const deductionHeaders = [
    ...orderedDeductions.filter((n) => !/lop\s*reversal\s*\(/i.test(String(n))),
  ];
  if (!deductionHeaders.some((n) => /^arr\.?\s*pf\b/i.test(String(n)))) {
    deductionHeaders.push("Arr.PF");
  }
  if (!deductionHeaders.some((n) => /^arr\.?\s*esi\b/i.test(String(n)))) {
    deductionHeaders.push("Arr.ESI");
  }
  const payTotalHeaders = [
    "Gross Pay",
    ...deductionHeaders,
    "Total Deductions",
    "Net Pay",
  ];
  const employerHeaders = [
    "PF Employer (EPF)",
    "EPS",
    "Admin Charges",
    "EDLI",
    "Total Employer PF",
    "ESI Employer",
    "Arr.PF",
    "Arr.ESI",
  ];
  const costHeaders = [
    ...(includeTotalCost ? ["Total Cost"] : []),
    ...(includeTotalCashFlow ? ["Total Cash Flow"] : []),
  ];
  const bankHeaders = ["Bank Name", "Account No", "IFSC"];
  const groups = [
    { title: "Employee Basic Details", count: basicHeaders.length, color: COLORS.basic },
    {
      title: "Fixed Structure — Earnings (E)",
      count: fixedEarningHeaders.length,
      color: COLORS.structure,
    },
    {
      title: "Fixed Structure — Employer Benefits (B)",
      count: benefitHeaders.length,
      color: COLORS.employer,
    },
    { title: "Attendance", count: attendanceHeaders.length, color: COLORS.attendance },
    ...(paidFixedHeaders.length
      ? [
          {
            title: "This Month — Fixed Earnings",
            count: paidFixedHeaders.length,
            color: COLORS.earnings,
          },
        ]
      : []),
    ...(variableHeaders.length
      ? [
          {
            title: "This Month — Variables",
            count: variableHeaders.length,
            color: "FFFFEDD5",
          },
        ]
      : []),
    {
      title: "Gross / Deductions / Net",
      count: payTotalHeaders.length,
      color: COLORS.earnings,
    },
    {
      title: "Employer Contributions (Prorated)",
      count: employerHeaders.length,
      color: COLORS.employer,
    },
    ...(costHeaders.length
      ? [{ title: "Cost & Cash Flow", count: costHeaders.length, color: "FFFEF9C3" }]
      : []),
    { title: "Bank Details", count: bankHeaders.length, color: COLORS.bank },
  ];

  const headersFinal = [
    ...basicHeaders,
    ...fixedEarningHeaders,
    ...benefitHeaders,
    ...attendanceHeaders,
    ...paidFixedHeaders,
    ...variableHeaders,
    ...payTotalHeaders,
    ...employerHeaders,
    ...costHeaders,
    ...bankHeaders,
  ];

  const ws = wb.addWorksheet(sheetName, {
    views: [{ state: "frozen", xSplit: 3, ySplit: 6, showGridLines: false }],
  });
  addTitleBlock(ws, companyName, title, metaLine, headersFinal.length);

  const groupValues = [];
  groups.forEach((g) => {
    groupValues.push(g.title);
    for (let i = 1; i < g.count; i += 1) groupValues.push("");
  });
  const groupRow = ws.addRow(groupValues);
  groupRow.height = 22;
  let col = 1;
  groups.forEach((g) => {
    const start = col;
    const end = col + g.count - 1;
    if (g.count > 1) {
      try {
        ws.mergeCells(5, start, 5, end);
      } catch {
        /* ignore */
      }
    }
    for (let c = start; c <= end; c += 1) {
      styleLightHeader(groupRow.getCell(c), g.color);
    }
    col = end + 1;
  });

  const headerRow = ws.addRow(headersFinal);
  headerRow.height = 36;
  headerRow.eachCell((cell, colNumber) => {
    let color = COLORS.basic;
    let cursor = 0;
    for (const g of groups) {
      if (colNumber > cursor && colNumber <= cursor + g.count) {
        color = g.color;
        break;
      }
      cursor += g.count;
    }
    styleLightHeader(cell, color);
    cell.alignment = { wrapText: true, vertical: "middle", horizontal: "center" };
  });

  const basicEnd = basicHeaders.length;
  const fixedEarnEnd = basicEnd + fixedEarningHeaders.length;
  const benefitEnd = fixedEarnEnd + benefitHeaders.length;
  const attEnd = benefitEnd + attendanceHeaders.length;
  const paidFixedEnd = attEnd + paidFixedHeaders.length;
  const variableEnd = paidFixedEnd + variableHeaders.length;
  const payEnd = variableEnd + payTotalHeaders.length;
  const emprEnd = payEnd + employerHeaders.length;
  const costEnd = emprEnd + costHeaders.length;

  const fallbackCostCashflow = (r) => {
    const empr = r.employerStatutory || {};
    const employerPf = Number(empr.totalEmployerPf) || 0;
    const esiEmpr = Number(empr.esiEmployer) || 0;
    const other = Number(r.employerBenefitsOther) || 0;
    const gross = Number(r.grossPay) || 0;
    const net = Number(r.netPay) || 0;
    const totalDed = Number(r.totalDeductions) || 0;
    const oneTimeDed = Number(r.oneTimeDeductions) || 0;
    const structuredDed = Math.max(0, roundMoney(totalDed - oneTimeDed));
    return {
      totalCost: roundMoney(gross + employerPf + esiEmpr + other),
      totalCashFlow: roundMoney(net + structuredDed + employerPf + esiEmpr),
    };
  };

  const mapAmt = (map, name) => roundMoney(map?.[name]);

  rows.forEach((r, idx) => {
    const empr = r.employerStatutory || {};
    const derived = fallbackCostCashflow(r);
    const fixedEarnMap =
      r.fixedEarningByName && Object.keys(r.fixedEarningByName).length
        ? r.fixedEarningByName
        : Object.fromEntries(
            Object.entries(r.fixedByName || {}).filter(([n]) =>
              fixedEarnings.some((x) => x === n)
            )
          );
    const benefitMap =
      r.benefitByName && Object.keys(r.benefitByName).length
        ? r.benefitByName
        : Object.fromEntries(
            Object.entries(r.fixedByName || {}).filter(
              ([n]) =>
                benefits.some((x) => x === n) ||
                /\(employer\)/i.test(n) ||
                /gratuity/i.test(n)
            )
          );
    const paidFixedMap =
      r.paidFixedByName && Object.keys(r.paidFixedByName).length
        ? r.paidFixedByName
        : r.paidByName || {};
    const paidVarMap = r.paidVariableByName || {};
    const paidAll = r.paidByName || {};

    const grossE =
      Number(r.grossSalaryE) ||
      fixedEarnings.reduce((s, n) => s + (Number(fixedEarnMap[n]) || 0), 0);
    const benefitsB =
      Number(r.totalBenefitsB) ||
      benefits.reduce((s, n) => s + (Number(benefitMap[n]) || 0), 0);
    const monthlyCtc =
      Number(r.monthlyCtc) || roundMoney(grossE + benefitsB);
    const annualCtc =
      Number(r.annualCtc) || roundMoney(monthlyCtc * 12);

    const values = [
      idx + 1,
      r.empCode || "",
      r.name || "",
      formatDateCell(r.doj),
      formatDateCell(r.doe),
      r.department || "",
      r.designation || "",
      r.branch || "",
      r.pan || "",
      r.aadhar || "",
      r.uan || "",
      r.pfAccountNo || "",
      r.esiNo || "",
      ...fixedEarnings.map((n) => mapAmt(fixedEarnMap, n)),
      roundMoney(grossE),
      ...benefits.map((n) => mapAmt(benefitMap, n)),
      roundMoney(benefitsB),
      roundMoney(monthlyCtc),
      roundMoney(annualCtc),
      r.calendarDays ?? "",
      r.paidDays ?? "",
      r.presentDays ?? "",
      roundMoney(r.lopDays),
      Number(r.lopReversalDays) || 0,
      ...paidFixed.map((n) => mapAmt(paidFixedMap, n)),
      ...variableHeaders.map((n) => {
        if (/^arrears$/i.test(String(n))) {
          return roundMoney(
            r.arrearsAmount ??
              paidVarMap.Arrears ??
              paidAll.Arrears ??
              paidAll["Arr. LOP Reversal"] ??
              0
          );
        }
        return mapAmt(paidVarMap[n] != null ? paidVarMap : paidAll, n);
      }),
      roundMoney(r.grossPay),
      ...deductionHeaders.map((n) => {
        if (/^arr\.?\s*pf\b/i.test(String(n))) {
          return roundMoney(r.arrPf ?? paidAll["Arr.PF"] ?? paidAll["Arr. PF"] ?? 0);
        }
        if (/^arr\.?\s*esi\b/i.test(String(n))) {
          return roundMoney(r.arrEsi ?? paidAll["Arr.ESI"] ?? paidAll["Arr. ESI"] ?? 0);
        }
        return mapAmt(paidAll, n);
      }),
      roundMoney(r.totalDeductions),
      roundMoney(r.netPay),
      roundMoney(empr.pfEmployerEpf),
      roundMoney(empr.eps),
      roundMoney(empr.adminCharges),
      roundMoney(empr.edli),
      roundMoney(empr.totalEmployerPf),
      roundMoney(empr.esiEmployer),
      roundMoney(r.arrPf ?? paidAll["Arr.PF"] ?? 0),
      roundMoney(r.arrEsi ?? paidAll["Arr.ESI"] ?? 0),
      ...(includeTotalCost ? [roundMoney(r.totalCost ?? derived.totalCost)] : []),
      ...(includeTotalCashFlow ? [roundMoney(r.totalCashFlow ?? derived.totalCashFlow)] : []),
      r.bankName || "",
      r.bankAccountNo || "",
      r.ifscCode || "",
    ];
    const dataRow = ws.addRow(values);
    dataRow.eachCell((cell, colNumber) => {
      applyDataBorder(cell);
      if (colNumber > basicEnd && colNumber <= benefitEnd) moneyFmt(cell);
      else if (colNumber > benefitEnd && colNumber <= attEnd) {
        cell.numFmt = "0";
        cell.alignment = { horizontal: "right" };
      } else if (colNumber > attEnd && colNumber <= costEnd) moneyFmt(cell);
    });
  });

  applyHeaderBasedColumnWidths(ws, headersFinal, { layout: "register" });
  return ws;
}

export function buildSalarySummarySheet(wb, { companyName, title, metaLine, summary }) {
  const ws = wb.addWorksheet("Salary Summary", {
    views: [{ state: "frozen", ySplit: 5, showGridLines: false }],
  });
  addTitleBlock(ws, companyName, title, metaLine, 4);

  const totals = summary?.totals || {};
  const header = ws.addRow(["Particulars", "Amount (₹)", "Category", "Notes"]);
  header.eachCell((cell) => styleLightHeader(cell, REPORT_COLORS.summary));

  const pushSection = (label) => {
    const row = ws.addRow([label, "", "", ""]);
    row.font = { bold: true, color: { argb: "FF1E3A5F" } };
    row.eachCell((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0F2FE" } };
      applyDataBorder(cell);
    });
  };

  const pushMoney = (name, amount, category, notes = "") => {
    const row = ws.addRow([name, roundMoney(amount), category, notes]);
    row.eachCell((cell, c) => {
      applyDataBorder(cell);
      if (c === 2) moneyFmt(cell);
    });
  };

  pushSection("Earnings by component");
  (summary?.earningsByComponent || []).forEach((e) =>
    pushMoney(e.name, e.amount, "Earning", "Must match Sheet 1 column total")
  );
  pushMoney("Gross Pay", totals.grossPay, "Total", "Sum of earnings");

  pushSection("Deductions by component");
  (summary?.deductionsByComponent || []).forEach((e) =>
    pushMoney(e.name, e.amount, "Deduction", "Must match Sheet 1 column total")
  );
  pushMoney("Total Deductions", totals.totalDeductions, "Total", "");
  pushMoney("Net Pay", totals.netPay, "Total", "Gross − Deductions");
  pushMoney("Bank Payment Total", totals.bankPaymentTotal, "Bank", "Equals Net Pay");

  pushSection("Statutory liabilities");
  pushMoney("PF (Employee)", totals.pfEmployee, "Statutory", "");
  pushMoney("PF (Employer)", totals.pfEmployer, "Statutory", "");
  pushMoney("ESI (Employee)", totals.esiEmployee, "Statutory", "");
  pushMoney("ESI (Employer)", totals.esiEmployer, "Statutory", "");
  pushMoney("Professional Tax", totals.professionalTax, "Statutory", "");
  pushMoney("TDS / Income Tax", totals.tds, "Statutory", "");
  pushMoney("Total Statutory Liabilities", totals.statutoryLiabilities, "Statutory", "");
  if (totals.totalCost != null) {
    pushMoney("Total Cost", totals.totalCost, "Cost", "Gross + employer benefits");
  }
  if (totals.totalCashFlow != null) {
    pushMoney(
      "Total Cash Flow",
      totals.totalCashFlow,
      "Cash",
      "Net + structured deductions + employer PF/ESI"
    );
  }

  pushSection("Headcount");
  const hc = ws.addRow(["Employees in report", Number(summary?.headcount) || 0, "Count", ""]);
  hc.eachCell((cell, c) => {
    applyDataBorder(cell);
    if (c === 2) {
      cell.numFmt = "0";
      cell.alignment = { horizontal: "right" };
    }
  });

  applyHeaderBasedColumnWidths(ws, ["Particulars", "Amount (₹)", "Category", "Notes"], {
    layout: "summary",
  });
  return ws;
}

export function buildBankSheet(wb, { companyName, title, metaLine, bankStatement, rows }) {
  const ws = wb.addWorksheet("Bank Statement", {
    views: [{ state: "frozen", ySplit: 5, showGridLines: false }],
  });
  addTitleBlock(ws, companyName, title, metaLine, 6);
  const bankCols = ["Sl No", "Name", "Bank Name", "Bank Account No", "IFSC Code", "Net Payable"];
  const bankHeaderRow = ws.addRow(bankCols);
  bankHeaderRow.eachCell((cell) => styleLightHeader(cell, REPORT_COLORS.bank));

  const bankRows =
    Array.isArray(bankStatement) && bankStatement.length
      ? bankStatement
      : (rows || []).map((r, i) => ({
          slNo: i + 1,
          name: r.name,
          bankName: r.bankName,
          bankAccountNo: r.bankAccountNo,
          ifscCode: r.ifscCode,
          netPayable: r.netPay,
        }));

  let bankTotal = 0;
  bankRows.forEach((r) => {
    const amt = roundMoney(r.netPayable);
    bankTotal += amt;
    const row = ws.addRow([
      r.slNo,
      r.name || "",
      r.bankName || "",
      r.bankAccountNo || "",
      r.ifscCode || "",
      amt,
    ]);
    row.eachCell((cell, c) => {
      applyDataBorder(cell);
      if (c === 6) moneyFmt(cell);
    });
  });

  const bankTot = ws.addRow(["", "", "", "", "TOTAL", roundMoney(bankTotal)]);
  bankTot.font = { bold: true };
  bankTot.eachCell((cell, c) => {
    styleLightHeader(cell, "FFF3F4F6");
    if (c === 6) moneyFmt(cell);
  });

  applyHeaderBasedColumnWidths(ws, bankCols, { layout: "bank" });
  return ws;
}

export function buildJvSheet(wb, {
  companyName,
  title,
  metaLine,
  jv,
  jvGroups,
  costMatch = null,
}) {
  const ws = wb.addWorksheet("Accounting Journal", {
    views: [{ state: "frozen", ySplit: 5, showGridLines: false }],
  });
  addTitleBlock(ws, companyName, title, metaLine, 6);

  if (costMatch) {
    const matchLabel = costMatch.matched
      ? "MATCHED"
      : `DIFFERENCE ₹${roundMoney(costMatch.difference)}`;
    const balLabel = costMatch.jvBalanced === false ? " · JV NOT BALANCED" : " · JV balanced";
    const matchRow = ws.addRow([
      `Cost match: Register total cost ₹${roundMoney(costMatch.registerTotalCost)} vs Journal debit ₹${roundMoney(
        costMatch.journalDebitTotal
      )} — ${matchLabel}${balLabel}`,
    ]);
    matchRow.font = {
      bold: true,
      color: { argb: costMatch.matched && costMatch.jvBalanced !== false ? "FF166534" : "FF991B1B" },
    };
    matchRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
          argb:
            costMatch.matched && costMatch.jvBalanced !== false ? "FFDCFCE7" : "FFFEE2E2",
        },
      };
    });
    try {
      ws.mergeCells(ws.rowCount, 1, ws.rowCount, 6);
    } catch {
      /* ignore */
    }

    const otherLines = costMatch.employerBenefitsOther || [];
    if (otherLines.length) {
      const detail = otherLines
        .map((l) => `${l.name}: ₹${roundMoney(l.amount)}`)
        .join(" · ");
      const detailRow = ws.addRow([
        `Employer benefits in Total Cost & JV (from salary structure, excl. PF/ESI employer): ${detail}`,
      ]);
      detailRow.font = { size: 9, italic: true, color: { argb: "FF374151" } };
      try {
        ws.mergeCells(ws.rowCount, 1, ws.rowCount, 6);
      } catch {
        /* ignore */
      }
    }
    ws.addRow([]);
  }

  const jvBlocks =
    Array.isArray(jvGroups) && jvGroups.length
      ? jvGroups
      : [{ groupLabel: "All Employees", ...(jv || {}) }];

  const jvHeaders = [
    "Sl No",
    "Account / Particulars",
    "Nature",
    "Debit (₹)",
    "Credit (₹)",
    "Narration",
  ];

  jvBlocks.forEach((block, blockIdx) => {
    if (blockIdx > 0) ws.addRow([]);
    const blockTitle = ws.addRow([block.groupLabel || `Group ${blockIdx + 1}`]);
    blockTitle.font = { bold: true, size: 11, color: { argb: "FF1E3A5F" } };
    blockTitle.eachCell((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: REPORT_COLORS.recon } };
    });
    try {
      ws.mergeCells(ws.rowCount, 1, ws.rowCount, 6);
    } catch {
      /* ignore */
    }

    const jvHeaderRow = ws.addRow(jvHeaders);
    jvHeaderRow.eachCell((cell) => styleLightHeader(cell, REPORT_COLORS.recon));

    const jvSummary = block.summary || {};
    (block.entries || []).forEach((e, i) => {
      const isDebit = String(e.nature).toLowerCase() === "debit";
      const amt = roundMoney(e.amount);
      const row = ws.addRow([
        i + 1,
        e.account,
        e.nature,
        isDebit ? amt : "",
        !isDebit ? amt : "",
        e.narration || "",
      ]);
      row.eachCell((cell, c) => {
        applyDataBorder(cell);
        if (c === 4 || c === 5) moneyFmt(cell);
        if (c === 3) {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: isDebit ? REPORT_COLORS.jvDebit : REPORT_COLORS.jvCredit },
          };
        }
      });
    });

    const totRow = ws.addRow([
      "",
      "TOTAL",
      "",
      roundMoney(jvSummary.debitTotal),
      roundMoney(jvSummary.creditTotal),
      jvSummary.balanced ? "Balanced" : "Check difference",
    ]);
    totRow.font = { bold: true };
    totRow.eachCell((cell, c) => {
      applyDataBorder(cell);
      if (c === 4 || c === 5) moneyFmt(cell);
      styleLightHeader(cell, "FFF3F4F6");
    });
  });

  applyHeaderBasedColumnWidths(ws, jvHeaders, { layout: "jv" });
  return ws;
}

export { formatPayMonthLabel };
